import { SxProps } from '@mui/material';
import { Button } from 'system/atoms';
import { useDrawer } from 'services/drawer/hooks';
import { DrawerComponentType } from 'services/drawer/types';
import { useState } from 'react';
import { useCampaignCreationStore } from 'modules/campaign-creation/stores/useCampaignCreationStore';
import { Paths } from 'services/navigation/paths';
import { getTitle } from 'modules/campaign-creation/utils/getTitle';
import { useSaveStateCampaign } from 'modules/campaign-creation/hooks/useSaveStateCampaign';
import { CampaignCreationSharedJobInformation } from 'modules/campaign-creation-shared/components';
import { CampaignCreationSharedJobInformationFormData } from 'modules/campaign-creation-shared/forms';

const jobDescriptionButtonSx: SxProps = { p: 0 };

export const CampaignCreationEditJobDescriptionStep: React.FC = () => {
  const state = useCampaignCreationStore((store) => store.state);
  const jobDescriptionParsedState = state?.jobDescriptionParsedState;
  const onEditJobDescription = useCampaignCreationStore(
    (store) => store.onEditJobDescription
  );
  const onFinishEditingJobDescription = useCampaignCreationStore(
    (store) => store.onFinishEditingJobDescription
  );
  const onEditJobDescriptionBack = useCampaignCreationStore(
    (store) => store.onEditJobDescriptionBack
  );

  const showViewJobDescriptionButton =
    !!state?.ATSJobDescriptionState?.selectedJob;
  const defaultJobDescriptionText =
    state?.noATSJobDescriptionState?.jobDescriptionText ??
    state?.ATSJobDescriptionState?.selectedJob?.jobDescriptionText ??
    '';

  // NOTE: This is used as a sort of cache for reseting the job description text
  const [jobDescriptionText] = useState(defaultJobDescriptionText);
  const [formValues, setFormValues] =
    useState<CampaignCreationSharedJobInformationFormData | null>(null);

  const defaultValues: CampaignCreationSharedJobInformationFormData = {
    jobTitle: jobDescriptionParsedState?.jobDescriptionParsed?.jobTitle || '',
    employerName:
      jobDescriptionParsedState?.jobDescriptionParsed?.employerName || '',
    location: jobDescriptionParsedState?.jobDescriptionParsed?.location || '',
    contractType:
      jobDescriptionParsedState?.jobDescriptionParsed?.contractType || '',
    additionalContext: jobDescriptionParsedState?.additionalContext || ''
  };

  // Save form values as they change
  useSaveStateCampaign({
    jobDescriptionParsedJobTitle: formValues?.jobTitle || undefined,
    jobDescriptionParsedEmployerName: formValues?.employerName || undefined,
    jobDescriptionParsedLocation: formValues?.location || undefined,
    jobDescriptionParsedContractType: formValues?.contractType || undefined,
    additionalContext: formValues?.additionalContext || undefined
  });

  const handleFormChange = (
    values: CampaignCreationSharedJobInformationFormData
  ) => {
    setFormValues(values);
  };

  const { openDrawer } = useDrawer();

  const handleViewJobDescription = () => {
    openDrawer({
      drawerContentType:
        DrawerComponentType.CampaignCreationJobDescriptionHtmlDrawer,
      drawerComponentProps: {
        originalDescription: defaultJobDescriptionText,
        jobDescriptionParsed: jobDescriptionParsedState?.jobDescriptionParsed,
        jobDescriptionText,
        hideCloseIconButton: true,
        onSave: (jobDescriptionText: string) => {
          onEditJobDescription({ jobDescriptionText });
        }
      }
    });
  };

  const handleSubmit = (
    values: CampaignCreationSharedJobInformationFormData
  ) => {
    onFinishEditingJobDescription({
      jobDescriptionParsed: {
        jobTitle: values.jobTitle,
        employerName: values.employerName,
        location: values.location,
        contractType: values.contractType || null
      },
      additionalContext: values.additionalContext || null
    });
  };

  return (
    <CampaignCreationSharedJobInformation
      title="New campaign"
      subtitle={jobDescriptionParsedState?.jobDescriptionParsed?.employerName}
      currentBreadcrumb={getTitle(state)}
      breadcrumbs={[{ name: 'Campaigns', path: Paths.CAMPAIGNS }]}
      onBack={() => onEditJobDescriptionBack()}
      actions={
        showViewJobDescriptionButton ? (
          <Button
            variant="tertiary"
            onClick={handleViewJobDescription}
            sx={jobDescriptionButtonSx}
          >
            Job description
          </Button>
        ) : undefined
      }
      defaultValues={defaultValues}
      onSubmit={handleSubmit}
      onFormChange={handleFormChange}
    />
  );
};
